import { ReactNode, HTMLAttributes } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: ReactNode;
}

export interface BreadcrumbProps extends HTMLAttributes<HTMLElement> {
  items: BreadcrumbItem[];
  showHome?: boolean;
}

export const Breadcrumb = ({
  items,
  showHome = true,
  className,
  ...props
}: BreadcrumbProps) => {
  const allItems: BreadcrumbItem[] = [];

  // Prepend home item if showHome is true and first item isn't Dashboard
  if (showHome && (items.length === 0 || items[0].label !== 'Dashboard')) {
    allItems.push({
      label: 'Dashboard',
      href: '/app/dashboard',
      icon: <HomeIcon className="h-4 w-4" />,
    });
  }

  allItems.push(...items);

  return (
    <nav aria-label="Breadcrumb" className={clsx(className)} {...props}>
      <ol className="flex items-center gap-1.5 text-sm">
        {allItems.map((item, index) => {
          const isLast = index === allItems.length - 1;

          return (
            <li key={index} className="flex items-center gap-1.5">
              {index > 0 && (
                <ChevronRightIcon
                  className="h-3 w-3 flex-shrink-0"
                  style={{ color: 'var(--color-text-muted)' }}
                />
              )}

              {isLast && !item.href ? (
                <span
                  className="font-medium"
                  style={{ color: 'var(--color-text-primary)' }}
                  aria-current="page"
                >
                  {item.icon && <span className="mr-1 inline-flex">{item.icon}</span>}
                  {item.label}
                </span>
              ) : (
                <Link
                  to={item.href ?? '#'}
                  className="inline-flex items-center transition-colors hover:text-primary-500"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {item.icon && <span className="mr-1 inline-flex">{item.icon}</span>}
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
